// ============================================================================
// T-IGPANEL-8A · «COMENTA PALABRA» SE CONFIGURA DESDE EL PANEL (migr 0105).
// Plan: ~/Proyectos Claude/Livinghouse/PLAN-TRAMO-8-PANEL-INSTAGRAM.md
//
// Una «automatización» = un destino (lo que se manda por DM) + sus palabras + dónde aplica
// (todas las publicaciones · una publicación · la próxima). La pantalla AGRUPA por destino
// lo que ya está en las tablas de la 0103; escribe SOLO por las RPC `guardar_automatizacion_ig`
// y `borrar_automatizacion_ig` (la guarda de dueño la pone la base, I-P-1). Las miniaturas
// llegan por la Edge `chatai-ig-publicaciones` (el token nunca baja al navegador, I-P-2).
// Sin dueño ⇒ solo lectura.
// ============================================================================
import { useEffect, useState } from "react";
import { Aviso, Boton, SkeletonLista } from "./piezas.tsx";
import type { ContextoPanel } from "./tipos.ts";

export type Alcance = "todas" | "una" | "proxima" | "inactiva";
export interface Automatizacion {
  slug: string; tema: string; url: string | null; plantilla: string | null;
  respuestas: string[]; excluidas: string[]; palabras: string[];
  /** T-IGPANEL-8B (0106): pedir que siga la cuenta / pedir correo antes del link, y sus textos (null = por defecto). */
  pideSeguir: boolean; pideCorreo: boolean;
  textoToque: string | null; textoSeguir: string | null; textoCorreo: string | null;
  alcance: Alcance; media: string | null;
  /** H1 (fiscal #270): las palabras por alcance tal como están en la base (puede haber más de uno). */
  grupos: { alcance: Exclude<Alcance, "inactiva">; media: string | null; palabras: string[] }[];
  /** H10: conteos exactos en la base (`count` head), no filas traídas. `abrieron` = DMs con al menos un clic. */
  cifras: { disparados: number; enviados: number; fallidos: number; abrieron: number };
}
export interface PublicacionIG { id: string; caption: string | null; tipo: string; miniatura: string | null; permalink: string | null; timestamp: string | null }

/** Igual que el worker y la base: sin tildes, MAYÚSCULAS. «Castilla» → «CASTILLA». */
export function normalizarPalabra(t: string): string {
  return String(t ?? "").normalize("NFD").replace(/[̀-ͯ]/g, "").toUpperCase().trim();
}
const lista = (t: string) => t.split(/[,\n]/).map((x) => normalizarPalabra(x)).filter(Boolean);

const PLANTILLA_DEFECTO = "{saludo} Gracias por comentar 🙌\n\nAquí tienes lo que te prometí sobre {tema}: {url}";
// T-IGPANEL-8B · los textos por defecto del paso fijo. ⚠️ Copia de `worker/src/canales/paso_ig.ts`
// (el panel no importa del worker); si cambian allá, cambian aquí.
export const TEXTO_TOQUE_DEFECTO = "{saludo} ¡Gracias por comentar! 🙌 Tengo lo de {tema} para ti. Responde SÍ y te lo mando por aquí.";
export const TEXTO_SEGUIR_DEFECTO = "Para mandártelo, síguenos en Instagram 💛 y luego toca «Ya te sigo».";
export const TEXTO_CORREO_DEFECTO = "¿A qué correo te lo enviamos también? Escríbelo aquí 📧";
/** Lo que recibe el cliente, mensaje a mensaje, con el paso encendido. Vacío = sin paso (va el DM de siempre). */
export function vistaPreviaPaso(p: { pideSeguir: boolean; pideCorreo: boolean; textoToque: string; textoSeguir: string;
  textoCorreo: string; tema: string; plantilla: string }): string[] {
  if (!p.pideSeguir && !p.pideCorreo) return [];
  const llenar = (t: string) => t.replace(/\{(\w+)\}/g, (m, k) => (
    { saludo: "¡Hola, @cliente!", tema: p.tema || "…", url: "https://…/chatai-ir/A1B2C3D4" } as Record<string, string>)[k] ?? m);
  const out = [llenar(p.textoToque.trim() || TEXTO_TOQUE_DEFECTO)];
  if (p.pideSeguir) out.push(`${llenar(p.textoSeguir.trim() || TEXTO_SEGUIR_DEFECTO)}  [botón: Ya te sigo]`);
  if (p.pideCorreo) out.push(llenar(p.textoCorreo.trim() || TEXTO_CORREO_DEFECTO));
  out.push(vistaPrevia(p.plantilla, p.tema));  // al final, el DM de siempre (con el link)
  return out;
}
export function vistaPrevia(plantilla: string | null, tema: string): string {
  const datos: Record<string, string> = { saludo: "¡Hola, @cliente!", tema: tema || "…", url: "https://…/chatai-ir/A1B2C3D4",
    whatsapp: "https://wa.me/…", ref: "A1B2C3D4" };
  return (plantilla?.trim() || PLANTILLA_DEFECTO).replace(/\{(\w+)\}/g, (m, k) => datos[k] ?? m);
}

async function q(p: PromiseLike<{ data: any; error: any }>) {
  const { data, error } = await p;
  if (error) throw error;
  return (data ?? []) as any[];
}

export async function leerAutomatizaciones(sb: any, tenantId: string): Promise<{ autos: Automatizacion[]; canal: { estado: string; prueba: boolean } | null }> {
  const desde = new Date(Date.now() - 30 * 86_400_000).toISOString();
  const [dest, pals, pubs, prox, canales] = await Promise.all([
    q(sb.from("respondedor_destinos").select("slug,tema,url,plantilla,respuestas_publicas,excluidas,pide_seguir,pide_correo,texto_toque,texto_seguir,texto_correo").eq("tenant_id", tenantId)),
    q(sb.from("respondedor_palabras").select("palabra,destino").eq("tenant_id", tenantId)),
    q(sb.from("respondedor_publicaciones").select("ig_media_id,palabra,destino").eq("tenant_id", tenantId)),
    q(sb.from("respondedor_proximas").select("destino,palabras,fijada_media").eq("tenant_id", tenantId)),
    q(sb.from("canales_instagram").select("estado,solo_usuarios").eq("tenant_id", tenantId)),
  ]);
  // H10: PostgREST corta en 1 000 filas; se cuenta en la base con `count: "exact", head: true`.
  const contar = async (slug: string, filtro: (x: any) => any) => {
    const { count, error } = await filtro(sb.from("comentarios_ig").select("id", { count: "exact", head: true })
      .eq("tenant_id", tenantId).eq("destino", slug).gte("creado_en", desde));
    if (error) throw error;
    return Number(count) || 0;
  };
  const cifrasDe = async (slug: string) => {
    const [disparados, enviados, fallidos, abrieron] = await Promise.all([
      contar(slug, (x) => x),
      contar(slug, (x) => x.eq("estado", "enviada")),
      // H2: `sin_url` (no había link) cuenta como NO enviado, igual que `error`.
      contar(slug, (x) => x.in("estado", ["error", "sin_url"])),
      contar(slug, (x) => x.gt("clics", 0)),
    ]);
    return { disparados, enviados, fallidos, abrieron };
  };
  const cifras = await Promise.all(dest.map((d: any) => cifrasDe(d.slug)));
  const autos = dest.map((d: any, i: number): Automatizacion => {
    const gen = pals.filter((p) => p.destino === d.slug).map((p) => p.palabra);
    const pub = pubs.filter((p) => p.destino === d.slug);
    const pend = prox.find((p) => p.destino === d.slug && !p.fijada_media);
    const grupos: Automatizacion["grupos"] = [];
    if (pend) grupos.push({ alcance: "proxima", media: null, palabras: pend.palabras ?? [] });
    for (const m of [...new Set(pub.map((p) => p.ig_media_id as string))]) {
      grupos.push({ alcance: "una", media: m, palabras: pub.filter((p) => p.ig_media_id === m).map((p) => p.palabra) });
    }
    if (gen.length) grupos.push({ alcance: "todas", media: null, palabras: gen });
    const base = { slug: d.slug, tema: d.tema, url: d.url ?? null, plantilla: d.plantilla ?? null,
      respuestas: d.respuestas_publicas ?? [], excluidas: d.excluidas ?? [], grupos, cifras: cifras[i],
      pideSeguir: d.pide_seguir === true, pideCorreo: d.pide_correo === true,
      textoToque: d.texto_toque ?? null, textoSeguir: d.texto_seguir ?? null, textoCorreo: d.texto_correo ?? null };
    const g = grupos[0];
    // TODAS las palabras del destino viajan al editor (nada se esconde ni se borra en silencio).
    const todas = [...new Set(grupos.flatMap((x) => x.palabras))];
    if (!g) return { ...base, palabras: [], alcance: "inactiva", media: null };
    return { ...base, palabras: todas, alcance: g.alcance, media: g.media };
  });
  const c = canales[0];
  return { autos, canal: c ? { estado: c.estado, prueba: (c.solo_usuarios ?? []).length > 0 } : null };
}

export async function leerPublicaciones(sb: any, tenantId: string, after?: string | null) {
  const { data, error } = await sb.functions.invoke("chatai-ig-publicaciones", { body: { tenant_id: tenantId, after: after ?? undefined } });
  if (error) {
    let msg = "No se pudieron traer las publicaciones.";
    try { msg = (await error.context?.json?.())?.error ?? msg; } catch { /* sin cuerpo */ }
    throw new Error(msg);
  }
  return data as { conectada: boolean; mensaje?: string; publicaciones?: PublicacionIG[]; siguiente?: string | null };
}

interface Borrador {
  slug: string; tema: string; url: string; plantilla: string; respuestas: [string, string, string];
  palabras: string; excluidas: string; alcance: "todas" | "una" | "proxima"; media: string | null;
  grupos: Automatizacion["grupos"];
  pideSeguir: boolean; pideCorreo: boolean; textoToque: string; textoSeguir: string; textoCorreo: string;
}
const nuevo = (): Borrador => ({ slug: "", tema: "", url: "", plantilla: "", respuestas: ["", "", ""], palabras: "",
  excluidas: "", alcance: "todas", media: null, grupos: [],
  pideSeguir: false, pideCorreo: false, textoToque: "", textoSeguir: "", textoCorreo: "" });
const deAuto = (a: Automatizacion): Borrador => ({ slug: a.slug, tema: a.tema, url: a.url ?? "", plantilla: a.plantilla ?? "",
  respuestas: [a.respuestas[0] ?? "", a.respuestas[1] ?? "", a.respuestas[2] ?? ""], palabras: a.palabras.join(", "),
  excluidas: a.excluidas.join(", "), alcance: a.alcance === "inactiva" ? "todas" : a.alcance, media: a.media,
  grupos: a.grupos, pideSeguir: a.pideSeguir, pideCorreo: a.pideCorreo,
  textoToque: a.textoToque ?? "", textoSeguir: a.textoSeguir ?? "", textoCorreo: a.textoCorreo ?? "" });

const NOMBRE_ALCANCE = (a: string, media: string | null) =>
  a === "todas" ? "todas las publicaciones" : a === "proxima" ? "la próxima publicación" : `la publicación ${media ?? "elegida"}`;
/** H1 · las palabras que hoy viven en OTRO alcance distinto del que se va a guardar (se moverían). */
export function palabrasQueSeMueven(b: { alcance: string; media: string | null; grupos: Automatizacion["grupos"] }): string[] {
  return [...new Set(b.grupos.filter((g) => !(g.alcance === b.alcance && (g.alcance !== "una" || g.media === b.media)))
    .flatMap((g) => g.palabras))];
}

const pct = (a: number, b: number) => (b > 0 ? `${Math.round((a / b) * 100)} %` : "—");
const ALCANCE_TXT: Record<Alcance, string> = { todas: "Todas las publicaciones", una: "Una publicación",
  proxima: "Esperando tu próxima publicación", inactiva: "Inactiva (sin palabras)" };

function Pildora({ children }: { children: string }) {
  return <span className="inline-flex items-center h-6 px-2 rounded-full bg-elevado border border-borde-suave text-[12px] font-medium">{children}</span>;
}

function Cuadricula({ ctx, elegida, onElegir }: { ctx: ContextoPanel; elegida: string | null; onElegir: (id: string) => void }) {
  const [pubs, setPubs] = useState<PublicacionIG[]>([]);
  const [sig, setSig] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [cargando, setCargando] = useState(true);
  const traer = async (after?: string | null) => {
    setCargando(true);
    try {
      const r = await leerPublicaciones(ctx.sb, ctx.tenantId, after);
      if (!r.conectada) setMsg(r.mensaje ?? "Instagram no está conectado.");
      else { setPubs((p) => [...p, ...(r.publicaciones ?? [])]); setSig(r.siguiente ?? null); }
    } catch (e: any) { setMsg(e?.message ?? "No se pudieron traer las publicaciones."); }
    finally { setCargando(false); }
  };
  useEffect(() => { void traer(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  if (msg) return <Aviso>{msg}</Aviso>;
  return (
    <div>
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" role="list" aria-label="Tus publicaciones">
        {pubs.map((p) => (
          <button key={p.id} type="button" role="listitem" aria-pressed={elegida === p.id}
            aria-label={`Publicación ${p.caption?.slice(0, 40) ?? p.id}`} onClick={() => onElegir(p.id)}
            className={`aspect-square rounded-lg overflow-hidden border-2 bg-elevado ${elegida === p.id ? "border-marca" : "border-transparent"}`}>
            {p.miniatura ? <img src={p.miniatura} alt="" className="w-full h-full object-cover" />
              : <span className="text-[11px] text-tinta-2 p-1 block">{p.tipo || "Publicación"}</span>}
          </button>
        ))}
      </div>
      {cargando && <p className="text-[13px] text-tinta-2 mt-2">Cargando publicaciones…</p>}
      {!cargando && sig && <Boton tam="sm" className="mt-2" onClick={() => void traer(sig)}>Ver más</Boton>}
    </div>
  );
}

function Editor({ ctx, inicial, onListo, onCancelar }: { ctx: ContextoPanel; inicial: Borrador; onListo: () => void; onCancelar: () => void }) {
  const [b, setB] = useState<Borrador>(inicial);
  const [error, setError] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const set = (k: Partial<Borrador>) => setB((x) => ({ ...x, ...k }));
  const mezcla = b.grupos.length > 1;
  const mover = palabrasQueSeMueven(b);
  const avisoMezcla = `Esta automatización tiene palabras en varios alcances; al guardar quedará solo en ${NOMBRE_ALCANCE(b.alcance, b.media)}.`;
  const guardar = async () => {
    setError(null);
    if (b.alcance === "una" && !b.media) { setError("Elige la publicación."); return; }
    // H2: sin link no se manda nada ⇒ no se deja guardar.
    if (!b.url.trim()) { setError("Falta el link: sin link no se le manda nada al cliente."); return; }
    if (mezcla && !window.confirm(`${avisoMezcla}\n\nPalabras que se moverían: ${mover.join(", ")}`)) return;
    setOcupado(true);
    const { error: e } = await ctx.sb.rpc("guardar_automatizacion_ig", {
      p_tenant: ctx.tenantId, p_slug: b.slug, p_tema: b.tema, p_url: b.url, p_plantilla: b.plantilla,
      p_respuestas: b.respuestas.map((x) => x.trim()).filter(Boolean), p_palabras: lista(b.palabras),
      p_ig_media_id: b.alcance === "una" ? b.media : null, p_excluidas: lista(b.excluidas),
      p_proxima: b.alcance === "proxima",
      p_pide_seguir: b.pideSeguir, p_pide_correo: b.pideCorreo,
      p_texto_toque: b.textoToque.trim() || null, p_texto_seguir: b.textoSeguir.trim() || null,
      p_texto_correo: b.textoCorreo.trim() || null });
    setOcupado(false);
    if (e) { setError(e.message ?? "No se pudo guardar."); return; }
    onListo();
  };
  const campo = "w-full rounded-lg border border-borde bg-superficie px-3 py-2 text-[14px]";
  return (
    <div className="rounded-2xl border border-borde-suave bg-superficie p-4 space-y-3" aria-label="Editor de automatización">
      {error && <Aviso tono="peligro">{error}</Aviso>}
      {mezcla && <Aviso>{avisoMezcla}{mover.length ? ` Se moverían: ${mover.join(", ")}.` : ""}</Aviso>}
      <fieldset>
        <legend className="text-[13px] font-medium mb-1">Dónde aplica</legend>
        {(["todas", "una", "proxima"] as const).map((a) => (
          <label key={a} className="mr-4 text-[14px]">
            <input type="radio" name="alcance" checked={b.alcance === a} onChange={() => set({ alcance: a })} /> {
              a === "todas" ? "Todas las publicaciones" : a === "una" ? "Una publicación" : "La próxima publicación"}
          </label>
        ))}
      </fieldset>
      {b.alcance === "una" && <Cuadricula ctx={ctx} elegida={b.media} onElegir={(id) => set({ media: id })} />}
      <label className="block text-[13px] font-medium">Palabras (separadas por coma)
        <input className={campo} value={b.palabras} onChange={(e) => set({ palabras: e.target.value })} placeholder="Castilla, sala" />
      </label>
      <div className="flex flex-wrap gap-1">{lista(b.palabras).map((p) => <Pildora key={p}>{p}</Pildora>)}</div>
      <label className="block text-[13px] font-medium">Palabras excluidas (si el comentario trae una, no se responde)
        <input className={campo} value={b.excluidas} onChange={(e) => set({ excluidas: e.target.value })} />
      </label>
      <label className="block text-[13px] font-medium">Tema
        <input className={campo} value={b.tema} onChange={(e) => set({ tema: e.target.value })} placeholder="la Sala Castilla" />
      </label>
      <label className="block text-[13px] font-medium">Link (https://…)
        <input className={campo} value={b.url} onChange={(e) => set({ url: e.target.value })} />
      </label>
      <label className="block text-[13px] font-medium">Mensaje del DM (opcional)
        <textarea className={campo} rows={3} value={b.plantilla} onChange={(e) => set({ plantilla: e.target.value })}
          placeholder={PLANTILLA_DEFECTO} />
      </label>
      <div>
        <p className="text-[13px] font-medium">{b.pideSeguir || b.pideCorreo ? "El mensaje con el link (va al final)" : "Así le llega por DM"}</p>
        <pre className="whitespace-pre-wrap text-[13px] text-tinta-2 bg-elevado rounded-lg p-2" aria-label="Vista previa del DM">{vistaPrevia(b.plantilla, b.tema)}</pre>
      </div>
      <fieldset aria-label="Antes del link">
        <legend className="text-[13px] font-medium mb-1">Antes de mandar el link</legend>
        <label className="block text-[14px]">
          <input type="checkbox" checked={b.pideSeguir} onChange={(e) => set({ pideSeguir: e.target.checked })} /> Pedir que siga la cuenta
        </label>
        <label className="block text-[14px]">
          <input type="checkbox" checked={b.pideCorreo} onChange={(e) => set({ pideCorreo: e.target.checked })} /> Pedir el correo
        </label>
        {(b.pideSeguir || b.pideCorreo) && (
          <div className="space-y-1 mt-1">
            <label className="block text-[13px] font-medium">Primer mensaje (el link va después de que responda)
              <textarea className={campo} rows={2} maxLength={500} value={b.textoToque}
                onChange={(e) => set({ textoToque: e.target.value })} placeholder={TEXTO_TOQUE_DEFECTO} />
            </label>
            {b.pideSeguir && <label className="block text-[13px] font-medium">Mensaje para pedir que siga
              <textarea className={campo} rows={2} maxLength={500} value={b.textoSeguir}
                onChange={(e) => set({ textoSeguir: e.target.value })} placeholder={TEXTO_SEGUIR_DEFECTO} />
            </label>}
            {b.pideCorreo && <label className="block text-[13px] font-medium">Mensaje para pedir el correo
              <textarea className={campo} rows={2} maxLength={500} value={b.textoCorreo}
                onChange={(e) => set({ textoCorreo: e.target.value })} placeholder={TEXTO_CORREO_DEFECTO} />
            </label>}
            <p className="text-[13px] font-medium">Así le llega, mensaje a mensaje</p>
            <ol className="text-[13px] text-tinta-2 bg-elevado rounded-lg p-2 list-decimal pl-6 space-y-1" aria-label="Vista previa de los pasos">
              {vistaPreviaPaso(b).map((t, i) => <li key={i} className="whitespace-pre-wrap">{t}</li>)}
            </ol>
            <p className="text-[12px] text-tinta-2">Si no sigue la cuenta tras 2 intentos, o el correo no sirve 2 veces, se le manda el link igual.</p>
          </div>
        )}
      </fieldset>
      <fieldset>
        <legend className="text-[13px] font-medium mb-1">Respuesta pública al comentario (hasta 3; se elige una al azar)</legend>
        {b.respuestas.map((r, i) => (
          <input key={i} aria-label={`Respuesta pública ${i + 1}`} className={`${campo} mb-1`} value={r} maxLength={300}
            placeholder={i === 0 ? "¡Te lo mandamos por DM! 📩" : ""}
            onChange={(e) => { const n = [...b.respuestas] as Borrador["respuestas"]; n[i] = e.target.value; set({ respuestas: n }); }} />
        ))}
      </fieldset>
      <div className="flex gap-2">
        <Boton tono="marca" disabled={ocupado} onClick={() => void guardar()}>Guardar</Boton>
        <Boton tono="fantasma" onClick={onCancelar}>Cancelar</Boton>
      </div>
    </div>
  );
}

export function InstagramComentarios({ ctx }: { ctx: ContextoPanel }) {
  const puedeEditar = ctx.rol === "dueno" || ctx.rol === "victorix";
  const [datos, setDatos] = useState<Awaited<ReturnType<typeof leerAutomatizaciones>> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [editando, setEditando] = useState<Borrador | null>(null);
  const [refresco, setRefresco] = useState(0);
  useEffect(() => {
    let vivo = true;
    leerAutomatizaciones(ctx.sb, ctx.tenantId)
      .then((d) => { if (vivo) { setDatos(d); setError(null); } })
      .catch(() => { if (vivo) setError("No se pudieron cargar las automatizaciones. Vuelve a entrar en un momento."); });
    return () => { vivo = false; };
  }, [ctx.sb, ctx.tenantId, refresco]);

  const borrar = async (a: Automatizacion) => {
    if (!window.confirm(`¿Borrar la automatización «${a.tema}»?`)) return;
    const { data, error: e } = await ctx.sb.rpc("borrar_automatizacion_ig", { p_tenant: ctx.tenantId, p_slug: a.slug });
    if (e) { setError(e.message ?? "No se pudo borrar."); return; }
    setAviso(data === "inactiva"
      ? "Ya tenía comentarios respondidos: se le quitaron las palabras y queda inactiva (el historial se conserva)."
      : "Automatización borrada.");
    setRefresco((n) => n + 1);
  };

  if (error && !datos) return <div className="p-4 sm:p-6"><Aviso tono="peligro">{error}</Aviso></div>;
  if (!datos) return <div className="p-4 sm:p-6"><SkeletonLista filas={3} /></div>;
  return (
    <div className="p-4 sm:p-6 max-w-3xl space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-[17px] font-semibold">Comenta PALABRA</h2>
          <p className="text-[13px] text-tinta-2">Cuando alguien comenta una palabra, le llega un DM con el link. Solo una vez por comentario.</p>
          <p className="text-[13px] mt-1" data-estado-canal>
            {datos.canal
              ? `Instagram: ${datos.canal.estado === "activo" ? "encendido" : "apagado"}${datos.canal.prueba ? " · modo prueba" : ""} (lo enciende Victorix)`
              : "Instagram no está conectado (lo conecta Victorix)."}
          </p>
        </div>
        {puedeEditar && !editando && <Boton tono="marca" onClick={() => setEditando(nuevo())}>Nueva</Boton>}
      </div>
      {!puedeEditar && <Aviso>Solo el dueño del negocio puede cambiar las automatizaciones. Tú las puedes ver.</Aviso>}
      {aviso && <Aviso tono="ok" onCerrar={() => setAviso(null)}>{aviso}</Aviso>}
      {error && <Aviso tono="peligro">{error}</Aviso>}
      {editando && puedeEditar && (
        <Editor ctx={ctx} inicial={editando} onCancelar={() => setEditando(null)}
          onListo={() => { setEditando(null); setAviso("Automatización guardada."); setRefresco((n) => n + 1); }} />
      )}
      {datos.autos.length === 0 && !editando && <p className="text-[14px] text-tinta-2">Todavía no hay automatizaciones.</p>}
      <ul className="space-y-2">
        {datos.autos.map((a) => (
          <li key={a.slug} className="rounded-2xl border border-borde-suave bg-superficie p-3" data-automatizacion={a.slug}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="text-[15px] font-medium truncate">{a.tema}</p>
                {a.grupos.length === 0 && <p className="text-[12px] text-tinta-2">{ALCANCE_TXT.inactiva}</p>}
                {a.grupos.map((g) => (
                  <div key={`${g.alcance}-${g.media ?? ""}`} className="mt-1" data-grupo={g.alcance}>
                    <p className="text-[12px] text-tinta-2">{ALCANCE_TXT[g.alcance]}{g.media ? ` · ${g.media}` : ""}</p>
                    <div className="flex flex-wrap gap-1">{g.palabras.map((p) => <Pildora key={p}>{p}</Pildora>)}</div>
                  </div>
                ))}
                {!a.url && <p className="text-[12px] font-medium text-peligro mt-1" data-sin-link>Sin link: no se manda nada</p>}
                <p className="text-[12px] text-tinta-2 mt-1">
                  Últimos 30 días: {a.cifras.disparados} comentarios · {a.cifras.enviados} DMs · {a.cifras.abrieron} abrieron el link
                  ({pct(a.cifras.abrieron, a.cifras.enviados)}) · {a.cifras.fallidos} no enviados
                </p>
              </div>
              {puedeEditar && (
                <div className="flex gap-1 shrink-0">
                  <Boton tam="sm" onClick={() => setEditando(deAuto(a))}>Editar</Boton>
                  <Boton tam="sm" tono="peligro" onClick={() => void borrar(a)}>Borrar</Boton>
                </div>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
